import React from 'react';
import { Link } from 'react-router-dom';
import { XIShadowsDown, XIShadowsLeft, XIShadowsRight, XIShadowsUp } from '@inmoment/the-kitchen';
import { Copy } from '../cookbook-ui/custom-components/Copy/Copy';
import { ShadowSquare } from '../cookbook-ui/visual-components/ShadowSquare/ShadowSquare';
import { SHADOW_DESCRIPTIONS } from './copy';
import { documentation, isAllCategory } from './Documentation';

export const capitalizeFirstLetter = (text: string) => {
  let capital = text[0].toUpperCase();
  let newText = text.substr(1, text.length);
  capital += newText;
  return capital;
};

export const formatAndCheckIfRequired = (propName: string) => {
  const LENGTH = propName.length;

  if (propName[LENGTH - 1] === '?') {
    let newPropName = propName.substr(0, LENGTH - 1);
    return { propName: newPropName, isRequired: false };
  } else {
    return { propName, isRequired: true };
  }
};

const returnLinkOnLargeList = (type: string) => {
  let path = '/Visuals';
  let typeString = type.toLowerCase().substr(0, type.length - 1);

  switch (type) {
    case 'SVGs':
    case 'Lotties':
      path = '/Graphics';
      break;
    case 'Icons':
      path = '/Icons';
      break;
    case 'AppIcons':
      typeString = 'app icon';
      path = '/Icons';
      break;
    case 'AppIconsWhite':
      typeString = 'white app icon';
      path = '/Icons';
      break;
    case 'Shadows':
      path = '/Shadows';
      break;
  }

  return <Link to={path}>See full list of {typeString} values.</Link>;
};

export function getLinkTo(name: string, props?: Omit<Parameters<typeof Link>[0], 'to'>): JSX.Element | string {
  // If exact match
  const exactMatch = documentation.byName[name];
  if (exactMatch && isAllCategory(exactMatch.category)) {
    return (
      <Link
        {...props}
        key={`${exactMatch.category}#${exactMatch.name}`}
        to={`${exactMatch.category}#${exactMatch.name}`}
        style={{ overflowWrap: 'anywhere' }}
      >
        {exactMatch.name}
      </Link>
    );
  }

  // Try to break apart type and look for known names
  const sanitizedName = name.replaceAll('React.', 'Reactdot');
  const matches = sanitizedName.match(/\W+|\w+/g);
  if (matches !== null && matches.length > 1) {
    return (
      <span>
        {matches.map((sanitizedMatch) => {
          const isReactType = sanitizedMatch.startsWith('Reactdot');
          const matchName = sanitizedMatch.replaceAll('Reactdot', 'React.');
          if (/^\w+$/.test(matchName)) {
            return getLinkTo(matchName, props);
          } else if (isReactType) {
            const reactlessName = matchName.replaceAll('React.', '');
            const searchText = reactlessName
              .replaceAll('HTML', 'Html')
              .replaceAll('DOM', 'Dom')
              .replaceAll('CSS', 'Css')
              .replaceAll(/([A-Z])/g, ' $1');

            return (
              <a
                key={matchName}
                // If you're looking at this, make sure the `site` link is still accurate
                href={encodeURI(`https://www.google.com/search?q=${searchText}+site:https://react.dev/reference/`)}
                target="_blank"
                rel="noreferrer"
                style={{ overflowWrap: 'anywhere' }}
              >
                {matchName}
              </a>
            );
          }
          return matchName;
        })}
      </span>
    );
  }

  // Otherwise just return name
  return name;
}

const DOCUMENTED_ELSEWHERE = ['Icons', 'SVGs', 'AppIcons', 'Lotties', 'Shadows', 'AppIconsWhite'];
const getAcceptedValuesFromEnum = (name: string) => {
  if (DOCUMENTED_ELSEWHERE.includes(name)) {
    return returnLinkOnLargeList(name);
  }
  const data = documentation.byName[name];
  return data ? data.type : null;
};

export const getStandardTypeValues = (type: string): React.ReactChild => {
  if (!type) {
    return '';
  }
  if (type.includes('any') || type.includes('number') || type.includes('string') || type.includes('(')) {
    return type;
  } else if (type === 'boolean') {
    return 'true | false';
  } else {
    return getAcceptedValuesFromEnum(type) ?? type;
  }
};

export const returnJSONs = () => documentation.legacyComponents;

export const removeQuestionMark = (propName: string) => {
  let length = propName.length;
  if (propName[length - 1] === '?') {
    return propName.substr(0, length - 1);
  } else {
    return propName;
  }
};

export interface ShadowObject {
  title: string;
  shadowArr: string[];
}
export const createShadowObject = (): ShadowObject[] => {
  const shadowObject = { XIShadowsDown, XIShadowsLeft, XIShadowsRight, XIShadowsUp };

  const typedShadowObject = shadowObject;
  const finalShadowArr: { title: string; shadowArr: string[] }[] = [];
  for (const _key in shadowObject) {
    const key = _key as keyof typeof shadowObject;
    let shadowDirection = { title: key, shadowArr: [] as string[] };
    for (const _level in typedShadowObject[key]) {
      const level = _level as unknown as keyof (typeof typedShadowObject)[keyof typeof typedShadowObject];
      shadowDirection.shadowArr.push(typedShadowObject[key][level]);
    }
    finalShadowArr.push(shadowDirection);
  }

  return finalShadowArr;
};

export const formatShadowsForTable = (shadowObj: ShadowObject['shadowArr']) => {
  return shadowObj.map((item, i) => {
    return [i, <ShadowSquare style={{ boxShadow: item }} />, <Copy>{item}</Copy>, SHADOW_DESCRIPTIONS[i]];
  });
};

export const getComplexComponentWarningString = (componentName: string) => {
  let name = '';
  switch (componentName) {
    case 'ListItem':
      name = 'List';
      break;
  }
  return `This component should be used solely inside the ${name} component. See ${name} component for implementation.`;
};
